import { BodyText, Button, Input, Separator, Skeleton } from "@/components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { QueryKeys } from "@/constants/Query";
import { usePatientSearch } from "@/features/Search/network/usePatientSearch";
import { captureError } from "@/lib/monitoring";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { getResourceType } from "../constants";
import { usePatientEverything } from "../network/useAdminFhir";
import type { AdminSearch } from "../schemas";
import { type FhirResource, formatFhirValue } from "../utils/fhir";
import { ResourceTypeSelect } from "./ResourceTypeSelect";

interface PatientBrowseProps {
  search: AdminSearch;
  onNavigate: (partial: Partial<AdminSearch>) => void;
  onInspect: (resourceType: string, resourceId: string) => void;
}

/**
 * Patient-scoped browse: find a patient, pick a resource type, then drill into
 * one of that patient's resources. Reuses the existing patient-search endpoint.
 */
export const PatientBrowse = ({
  search,
  onNavigate,
  onInspect,
}: PatientBrowseProps) => {
  const [firstName, setFirstName] = useState(search.firstName ?? "");
  const [lastName, setLastName] = useState(search.lastName ?? "");
  const [dob, setDob] = useState(search.dob ?? "");
  const [mrn, setMrn] = useState(search.mrn ?? "");

  const {
    data: patientData,
    isFetching: isFetchingPatients,
    isLoading: isLoadingPatients,
    isFetched: isFetchedPatients,
    isError: isErrorPatients,
    error: patientError,
  } = usePatientSearch({
    patientId: search.mrn,
    patientFirstName: search.firstName,
    patientLastName: search.lastName,
    dob: search.dob,
  });

  // Surface search failures instead of rendering an empty table (which is
  // indistinguishable from "no matches").
  useEffect(() => {
    if (patientError) captureError(patientError);
  }, [patientError]);

  const resourceConfig = getResourceType(search.resourceType);
  const isPatientType = search.resourceType === "Patient";

  const {
    data: allResources,
    isLoading: isLoadingResources,
    isFetching: isFetchingResources,
  } = usePatientEverything(search.patientId);

  // Force a refetch of all admin FHIR data for the current patient
  // (everything + any open resource/history), bypassing staleTime.
  const queryClient = useQueryClient();
  const refreshPatientData = () =>
    queryClient.invalidateQueries({ queryKey: QueryKeys.Admin.DEFAULT });

  // Only offer resource types the patient actually has (∩ supported types).
  const availableTypes = allResources
    ? [...new Set(allResources.map((resource) => resource.resourceType))]
    : undefined;

  const resources = (allResources ?? []).filter(
    (resource) => resource.resourceType === search.resourceType,
  );

  const submitPatientSearch = (event: React.FormEvent) => {
    event.preventDefault();
    onNavigate({
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      dob: dob || undefined,
      mrn: mrn || undefined,
      // Reset any prior selection when starting a new search.
      patientId: undefined,
      resourceType: undefined,
      resourceId: undefined,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={submitPatientSearch}
      >
        <LabeledInput
          label="First name"
          value={firstName}
          onChange={setFirstName}
        />
        <LabeledInput
          label="Last name"
          value={lastName}
          onChange={setLastName}
        />
        <LabeledInput label="DOB (YYYY-MM-DD)" value={dob} onChange={setDob} />
        <LabeledInput label="MRN" value={mrn} onChange={setMrn} />
        <Button type="submit" isLoading={isFetchingPatients} className="w-fit">
          Search
        </Button>
      </form>

      {isErrorPatients && (
        <BodyText size="sm" textColor="error">
          Something went wrong searching for patients. Please try again.
        </BodyText>
      )}

      {!isErrorPatients && (isLoadingPatients || isFetchedPatients) && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>DOB</TableHead>
              <TableHead>MRN</TableHead>
              <TableHead>Patient id</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingPatients ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            ) : (
              (patientData?.results ?? []).map((patient) => {
                const isSelected = patient.patientId === search.patientId;
                return (
                  <TableRow
                    key={patient.patientId}
                    // Highlight the patient currently being inspected in the
                    // brand green (the table stays visible for re-selection).
                    className={cn(
                      isSelected &&
                        "bg-primary-green/15 hover:bg-primary-green/15",
                    )}
                  >
                    <TableCell>
                      {patient.firstName} {patient.lastName}
                    </TableCell>
                    <TableCell>{patient.dob}</TableCell>
                    <TableCell>{patient.mrn}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {patient.patientId}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() =>
                          onNavigate({
                            patientId: patient.patientId,
                            resourceType: undefined,
                            resourceId: undefined,
                          })
                        }
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      )}

      {search.patientId && (
        <>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <BodyText bold>
                Resources for patient{" "}
                <span className="font-mono text-sm">{search.patientId}</span>
              </BodyText>
              <Button
                variant="secondary"
                size="sm"
                isLoading={isFetchingResources}
                onClick={refreshPatientData}
                className="w-fit"
              >
                <span className="flex items-center gap-1.5">
                  <RefreshCw size={14} />
                  Refresh
                </span>
              </Button>
            </div>
            <div className="max-w-md">
              <ResourceTypeSelect
                value={search.resourceType}
                onChange={(resourceType) =>
                  onNavigate({ resourceType, resourceId: undefined })
                }
                placeholder={
                  isLoadingResources
                    ? "Loading resources…"
                    : "Select a resource type to list"
                }
                allowedTypes={availableTypes}
                disabled={isLoadingResources}
              />
            </div>

            {isPatientType && (
              <Button
                className="w-fit"
                onClick={() => onInspect("Patient", search.patientId as string)}
              >
                Inspect patient record
              </Button>
            )}

            {search.resourceType && !isPatientType && (
              <ResourceList
                isLoading={isLoadingResources}
                resources={resources}
                emptyLabel={`No ${resourceConfig?.label ?? search.resourceType} resources found for this patient.`}
                onInspect={(id) => onInspect(search.resourceType as string, id)}
                selectedId={search.resourceId}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

const LabeledInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="space-y-1">
    <BodyText size="sm" textColor="secondary">
      {label}
    </BodyText>
    <Input value={value} onChange={(event) => onChange(event.target.value)} />
  </div>
);

const ResourceList = ({
  isLoading,
  resources,
  emptyLabel,
  onInspect,
  selectedId,
}: {
  isLoading: boolean;
  resources: FhirResource[];
  emptyLabel: string;
  onInspect: (id: string) => void;
  selectedId?: string;
}) => {
  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (resources.length === 0)
    return (
      <BodyText size="sm" textColor="secondary">
        {emptyLabel}
      </BodyText>
    );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Id</TableHead>
          <TableHead>Summary</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {resources.map((resource) => (
          <TableRow
            key={resource.id}
            // Highlight the resource currently open in the detail/history panel.
            className={cn(
              resource.id === selectedId &&
                "bg-primary-green/15 hover:bg-primary-green/15",
            )}
          >
            <TableCell className="font-mono text-xs">{resource.id}</TableCell>
            <TableCell>
              {formatFhirValue(
                resource.code ?? resource.category ?? resource.status,
              )}
            </TableCell>
            <TableCell>
              <Button
                variant="link"
                size="sm"
                onClick={() => resource.id && onInspect(resource.id)}
              >
                Inspect
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
